import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI } from '../services/api';
import { Message, Conversation, Language } from '../types';
import { useTranslation } from '../utils/translations';
import ChatSidebar from './ChatSidebar';
import ChatMessage from './ChatMessage';
import SergicLogo from './SergicLogo';
import MobileHeader from './MobileHeader';
import DesktopHeader from './DesktopHeader';
import LoadingAnimation from './LoadingAnimation';
import TypewriterMarkdown from './TypewriterMarkdown';
import QuickQuestions from './QuickQuestions';
import Toast, { ToastType } from './Toast';
import { useAsyncChat } from '../hooks/useAsyncChat';

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('fr');
  const [typingMessage, setTypingMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingConversationId, setTypingConversationId] = useState<number | null>(null);
  const [loadingConversationId, setLoadingConversationId] = useState<number | null>(null);
  const [asyncProcessingConversationId, setAsyncProcessingConversationId] = useState<number | null>(null);
  const [asyncProcessingMessage, setAsyncProcessingMessage] = useState<string>('');
  const [asyncProcessingStartTime, setAsyncProcessingStartTime] = useState<number | null>(null);
  const [asyncElapsedSeconds, setAsyncElapsedSeconds] = useState<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslation(language);
  
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Async chat hook
  const asyncChat = useAsyncChat();

  // Load conversations on component mount
  useEffect(() => {
    if (user) {
      loadConversations();
    } else {
      console.log('❌ User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [user, navigate]);

  // Update elapsed time for async processing
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (asyncProcessingStartTime && asyncChat.isPolling) {
      intervalId = setInterval(() => {
        const elapsed = Math.floor((Date.now() - asyncProcessingStartTime) / 1000);
        setAsyncElapsedSeconds(elapsed);
        
        // Update the processing message with elapsed time
        setMessages(prev => prev.map(msg => {
          if (msg.content.includes('Traitement en cours')) {
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
            return {
              ...msg,
              content: `🔄 Traitement en cours... (${timeStr})`
            };
          }
          return msg;
        }));
      }, 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [asyncProcessingStartTime, asyncChat.isPolling]);

  // Handle async chat responses
  useEffect(() => {
    if (asyncChat.status === 'completed' && asyncChat.result) {
      console.log('✅ Async response received:', asyncChat.result);
      
      // Remove processing message
      setMessages(prev => prev.filter(msg => !msg.content.includes('Traitement en cours')));
      
      // Clear processing state
      setAsyncProcessingConversationId(null);
      setAsyncProcessingMessage('');
      setAsyncProcessingStartTime(null);
      setAsyncElapsedSeconds(0);
      
      // Reload messages from database to get the saved conversation
      if (currentConversationId) {
        loadMessages(currentConversationId);
      } else {
        // If no conversation ID, reload conversations to get the new one
        loadConversations();
      }
      
      // Reset async chat state
      asyncChat.reset();
      
      showToast('Réponse reçue !', 'success');
    } else if (asyncChat.status === 'failed' && asyncChat.error) {
      console.error('❌ Async request failed:', asyncChat.error);
      
      // Remove processing message
      setMessages(prev => prev.filter(msg => !msg.content.includes('Traitement en cours')));
      
      // Clear processing state
      setAsyncProcessingConversationId(null);
      setAsyncProcessingMessage('');
      setAsyncProcessingStartTime(null);
      setAsyncElapsedSeconds(0);
      
      // Determine error message based on error type
      let errorContent = 'Désolé, une erreur est survenue lors du traitement de votre question. Veuillez réessayer.';
      if (asyncChat.error.includes('timeout') || asyncChat.error.includes('trop de temps')) {
        errorContent = 'La requête a pris trop de temps. Veuillez réessayer avec une question plus simple ou reformuler votre demande.';
      } else if (asyncChat.error.includes('network') || asyncChat.error.includes('Network')) {
        errorContent = 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
      } else if (asyncChat.error.includes('rate limit') || asyncChat.error.includes('quota')) {
        errorContent = 'Limite de requêtes atteinte. Veuillez réessayer dans quelques instants.';
      }
      
      // Add error message
      const errorMessage: Message = {
        id: Date.now(),
        content: errorContent,
        isUserMessage: false,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
      
      // Reset async chat state
      asyncChat.reset();
      
      showToast('Erreur lors du traitement', 'error');
    }
  }, [asyncChat.status, asyncChat.result, asyncChat.error, asyncChat.reset, currentConversationId]);

  // Load messages when conversation changes
  useEffect(() => {
    if (currentConversationId) {
      // Only load messages if this conversation is not currently being processed asynchronously
      if (asyncProcessingConversationId !== currentConversationId) {
        loadMessages(currentConversationId);
      } else {
        // If this conversation is being processed, show the processing state
        setMessages(prev => {
          // Show the user message and processing indicator
          const userMessage: Message = {
            id: Date.now() - 1,
            content: asyncProcessingMessage,
            isUserMessage: true,
            createdAt: new Date().toISOString(),
          };
          
          const processingMessage: Message = {
            id: Date.now(),
            content: '🔄 Traitement en cours...',
            isUserMessage: false,
            createdAt: new Date().toISOString(),
          };
          
          return [userMessage, processingMessage];
        });
      }
    } else {
      setMessages([]);
    }
    
    // Reset typing state when switching conversations
    console.log('🔄 Conversation changed, resetting typing state:', { 
      oldTypingConversationId: typingConversationId, 
      newCurrentConversationId: currentConversationId 
    });
    setIsTyping(false);
    setTypingMessage('');
    setTypingConversationId(null);
    setLoading(false);
    setLoadingConversationId(null);
  }, [currentConversationId, asyncProcessingConversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, typingMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const conversationsData = await chatAPI.getConversations();
      setConversations(conversationsData);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (conversationId: number) => {
    try {
      const messagesData = await chatAPI.getMessages(conversationId);
      setMessages(messagesData);
      // Reset typing state when switching conversations
      setIsTyping(false);
      setTypingMessage('');
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading || isTyping) return;

    // Check if user is authenticated
    if (!user) {
      console.error('❌ User not authenticated');
      navigate('/login');
      return;
    }

    const messageToSend = inputMessage.trim();
    setInputMessage('');
    setLoading(true);
    setLoadingConversationId(currentConversationId || null);

    console.log('🚀 Sending message:', messageToSend);

    // Add user message to UI immediately
    const userMessage: Message = {
      id: Date.now(),
      content: messageToSend,
      isUserMessage: true,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Generate conversation title for display (will be updated after response)
    const conversationTitle = generateConversationTitle(messageToSend);

    try {
      // Use async request for ALL messages to avoid timeouts
      console.log('🔄 Starting async request');
      
      // Start async request using the standard sendMessage endpoint (now async)
      const response = await chatAPI.sendMessage({
        message: messageToSend,
        conversationId: currentConversationId || undefined,
      });

      console.log('✅ Async request started:', response);

      // Update conversation ID if it's a new conversation
      if (!currentConversationId && response.conversationId) {
        setCurrentConversationId(response.conversationId);
        
        // Update conversation title in database
        if (response.conversationTitle) {
          try {
            await chatAPI.updateConversationTitle(response.conversationId, response.conversationTitle);
          } catch (error) {
            console.error('Failed to update conversation title:', error);
          }
        }
        
        // Reload conversations to get the updated title
        await loadConversations();
      }

      // Update conversation title if provided
      if (response.conversationTitle) {
        setConversations(prev => 
          prev.map(conv => 
            conv.id === (currentConversationId || response.conversationId)
              ? { ...conv, title: response.conversationTitle! }
              : conv
          )
        );
      }

      // Start async polling using the requestId from response
      if (response.requestId) {
        // Start polling with the requestId returned by sendMessage
        asyncChat.startPolling(response.requestId);
        
        // Set processing state for this conversation
        const conversationId = response.conversationId || currentConversationId || Date.now();
        setAsyncProcessingConversationId(conversationId);
        setAsyncProcessingMessage(messageToSend);
        setAsyncProcessingStartTime(Date.now());
        setAsyncElapsedSeconds(0);
        
        // Show processing message (will be updated with elapsed time by useEffect)
        const processingMessage: Message = {
          id: Date.now() + 1,
          content: '🔄 Traitement en cours... (0s)',
          isUserMessage: false,
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, processingMessage]);
      } else {
        // Fallback: if no requestId, something went wrong
        throw new Error('No request ID received from server');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the user message if there was an error
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      // Reset loading state on error
      setLoading(false);
      setLoadingConversationId(null);
      
      // Show error toast
      if (error instanceof Error) {
        if (error.message.includes('timeout') || error.message.includes('TIMEOUT')) {
          showToast(t('timeout.error'), 'error');
        } else if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
          showToast(t('connection.error'), 'error');
        } else {
          showToast(t('error.message'), 'error');
        }
      } else {
        showToast(t('error.message'), 'error');
      }
    } finally {
      setLoading(false);
      setLoadingConversationId(null);
    }
  };

  const handleTypingComplete = async () => {
    console.log('✅ Typing animation complete, replacing thinking message with response');
    
    // Replace the "Sergic Réfléchit" message with the actual AI response
    setMessages(prev => {
      const updatedMessages = [...prev];
      const lastMessageIndex = updatedMessages.length - 1;
      
      // Check if the last message is the thinking message
      if (lastMessageIndex >= 0 && 
          updatedMessages[lastMessageIndex].content.includes('Sergic Réfléchit')) {
        // Replace the thinking message with the actual response
        updatedMessages[lastMessageIndex] = {
          id: updatedMessages[lastMessageIndex].id,
          content: typingMessage,
          isUserMessage: false,
          createdAt: new Date().toISOString(),
        };
      } else {
        // If no thinking message found, add the response normally
        const aiMessage: Message = {
          id: Date.now() + 1,
          content: typingMessage,
          isUserMessage: false,
          createdAt: new Date().toISOString(),
        };
        updatedMessages.push(aiMessage);
      }
      
      return updatedMessages;
    });
    
    // Reset typing state
    setIsTyping(false);
    setTypingMessage('');
    setTypingConversationId(null);
    // Reset loading state
    setLoading(false);
    setLoadingConversationId(null);
    
    // Reload conversations to ensure titles are up to date
    await loadConversations();
  };

  const handleNewConversation = async () => {
    try {
      // Create a new empty conversation
      const response = await chatAPI.createConversation();
      const newConversation = response.conversation;
      
      // Update state
      setCurrentConversationId(newConversation.id);
      setMessages([]);
      setSidebarOpen(false);
      setIsTyping(false);
      setTypingMessage('');
      setTypingConversationId(null);
      setLoading(false);
      setLoadingConversationId(null);
      
      // Refresh conversations list
      await loadConversations();
      
      // Focus on input field after creating new conversation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  const handleDeleteConversation = async (conversationId: number) => {
    try {
      await chatAPI.deleteConversation(conversationId);
      if (currentConversationId === conversationId) {
        setMessages([]);
        setCurrentConversationId(null);
      }
      await loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // const handleLogout = () => {
  //   logout();
  //   navigate('/login');
  // };

  const handleQuickQuestion = (question: string) => {
    setInputMessage(question);
    // Auto-submit the question
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        form.requestSubmit();
      }
    }, 100);
  };

  // Generate conversation title from message
  const generateConversationTitle = (message: string): string => {
    // Take first 50 characters and clean up
    let title = message.trim();
    if (title.length > 50) {
      title = title.substring(0, 50).trim() + '...';
    }
    // Remove common question words at the beginning
    const questionWords = ['Qu\'est-ce que', 'Comment', 'Pourquoi', 'Quand', 'Où', 'Qui', 'Que', 'Quel', 'Quelle', 'Quels', 'Quelles'];
    for (const word of questionWords) {
      if (title.toLowerCase().startsWith(word.toLowerCase())) {
        title = title.substring(word.length).trim();
        break;
      }
    }
    return title || 'Nouvelle conversation';
  };

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <ChatSidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        onConversationSelect={(conversationId) => {
          setCurrentConversationId(conversationId);
        }}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        language={language}
        asyncProcessingConversationId={asyncProcessingConversationId}
      />

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <div className="lg:hidden">
          <MobileHeader 
            onMenuClick={() => setSidebarOpen(true)}
            language={language}
            onLanguageChange={setLanguage}
          />
        </div>
        
        {/* Desktop Header */}
        <div className="hidden lg:block">
          <DesktopHeader 
            language={language}
            onLanguageChange={setLanguage}
          />
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-4">
          {messages.length === 0 && !isTyping ? (
            <div className="text-center text-gray-500 mt-8">
              <div className="mb-4">
                <SergicLogo className="h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium mb-2">{t('welcome')}</h3>
              <p className="text-sm mb-6">{t('welcome.subtitle')}</p>
              <QuickQuestions 
                onQuestionClick={handleQuickQuestion}
                language={language}
                disabled={loading || isTyping}
              />
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage 
                  key={message.id} 
                  message={message} 
                  language={language}
                />
              ))}
              
              {/* Async processing indicator */}
              {asyncProcessingConversationId === currentConversationId && asyncChat.isPolling && (
                <div className="flex items-center justify-center py-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Traitement en cours...
                        </p>
                        <p className="text-xs text-blue-600">
                          Cette question complexe peut prendre quelques minutes
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Loading animation - ONLY for current conversation */}
          {loading && !isTyping && currentConversationId && loadingConversationId === currentConversationId && (
            <LoadingAnimation />
          )}

          {/* Typing animation - ONLY for current conversation */}
          {isTyping && typingConversationId === currentConversationId && (
            <div className="flex justify-start px-2 sm:px-0">
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 max-w-[85%] sm:max-w-sm lg:max-w-2xl">
                <TypewriterMarkdown
                  text={typingMessage}
                  speed={30}
                  onComplete={handleTypingComplete}
                  className="text-sm text-gray-900"
                />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="bg-white border-t border-gray-200 p-2 sm:p-4">
          <form onSubmit={handleSendMessage} className="flex space-x-1 sm:space-x-4">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!loading && !isTyping && inputMessage.trim()) {
                      handleSendMessage(e);
                    }
                  }
                }}
                placeholder={t('type.message')}
                className="input-field w-full text-sm sm:text-base py-2 sm:py-3 pr-8 sm:pr-10"
                disabled={loading || isTyping}
                maxLength={1000}
                autoFocus
              />
              <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                {inputMessage.length}/1000
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || isTyping || !inputMessage.trim()}
              className="btn-primary px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-base flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] sm:min-w-0"
              title={inputMessage.trim() ? t('send') : 'Tapez un message pour envoyer'}
            >
              {loading || isTyping ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span className="hidden sm:inline">{t('send')}</span>
                  <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </>
              )}
            </button>
          </form>
          <div className="mt-1 sm:mt-2 text-xs text-gray-500 text-center px-2">
            <span className="hidden sm:inline">Appuyez sur <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Entrée</kbd> pour envoyer, <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Shift + Entrée</kbd> pour une nouvelle ligne</span>
            <span className="sm:hidden">Appuyez sur <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Entrée</kbd> pour envoyer</span>
          </div>
        </div>
      </div>
      
      {/* Toast notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        language={language}
      />
    </div>
  );
};

export default Chat;
